import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, ScanLine, Play, Undo2, Loader2, AlertTriangle,
  CheckCircle2, History,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { API } from "../../config/api";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const STAGE_LABELS = {
  site_visit_scheduled: "Scheduled",
  site_visit: "Done (first visit)",
  revisit: "Done (revisit)",
};
const STATUS_TONE = {
  scheduled: "bg-violet-50 text-violet-700 border-violet-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function VisitSynthesisBackfillCard() {
  const [dryrun, setDryrun] = useState(null);
  const [loadingDry, setLoadingDry] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [runs, setRuns] = useState([]);
  const [showRuns, setShowRuns] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(null);
  const [rollbackText, setRollbackText] = useState("");

  const loadRuns = async () => {
    try {
      const res = await axios.get(`${API}/admin/visit-synthesis-backfill/runs?limit=20`);
      setRuns(res.data.runs || []);
    } catch {}
  };
  useEffect(() => { loadRuns(); }, []);

  const runDryRun = async () => {
    setLoadingDry(true);
    setDryrun(null);
    setApplyResult(null);
    try {
      const res = await axios.post(`${API}/admin/visit-synthesis-backfill/dry-run`);
      setDryrun(res.data);
      toast.success(`Dry-run complete — ${fmt(res.data.totals.orphan_leads)} orphan leads to heal`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dry-run failed");
    } finally {
      setLoadingDry(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    setShowApplyConfirm(false);
    setConfirmText("");
    try {
      const res = await axios.post(`${API}/admin/visit-synthesis-backfill/apply?confirm=CONFIRM`);
      setApplyResult(res.data);
      toast.success(`Apply complete — ${fmt(res.data.totals.created)} visits created`);
      setDryrun(null);
      loadRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const runRollback = async (run) => {
    setRollingBack(run.run_id);
    setShowRollbackConfirm(null);
    setRollbackText("");
    try {
      const res = await axios.post(
        `${API}/admin/visit-synthesis-backfill/rollback?run_id=${run.run_id}&confirm=ROLLBACK`
      );
      toast.success(`Rolled back — ${fmt(res.data.totals.deleted)} synthesised visits removed`);
      loadRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rollback failed");
    } finally {
      setRollingBack(null);
    }
  };

  const t = dryrun?.totals;
  const orphans = t?.orphan_leads ?? 0;

  return (
    <Card className="border-violet-200" data-testid="visit-synthesis-backfill-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="w-4 h-4 text-violet-600" />
          Visit-Record Synthesis Backfill
          <Badge className="bg-violet-100 text-violet-800 border-violet-200">Hot fix · Path A</Badge>
        </CardTitle>
        <p className="text-xs text-slate-600 mt-1">
          Creates one minimal <code>site_visits</code> record per lead whose stage is
          <code className="mx-1">site_visit_scheduled</code> /
          <code className="mx-1">site_visit</code> /
          <code className="mx-1">revisit</code> but has no matching visit document.
          Heals the data so the AI Site Visit Quality Report, the /site-visits page, and
          rep feedback all see the same numbers. Records carry
          <code className="mx-1">is_synthesised: true</code> + <code>synthesis_run_id</code>
          so rollback removes only this batch. Idempotent.
        </p>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={runDryRun}
            disabled={loadingDry || applying}
            data-testid="dryrun-visit-synthesis"
            className="bg-sky-600 hover:bg-sky-700"
          >
            {loadingDry ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />}
            Dry Run
          </Button>
          <Button
            onClick={() => setShowApplyConfirm(true)}
            disabled={!dryrun || orphans === 0 || applying}
            data-testid="apply-visit-synthesis"
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
          >
            {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Apply ({fmt(orphans)})
          </Button>
          <Button
            variant="outline"
            onClick={() => { setShowRuns(!showRuns); loadRuns(); }}
            data-testid="show-runs-visit-synthesis"
          >
            <History className="w-4 h-4 mr-2" />
            {showRuns ? "Hide" : "Show"} runs ({runs.length})
          </Button>
        </div>

        {dryrun && (
          <div className="border border-sky-200 bg-sky-50 rounded-lg p-3" data-testid="vs-dryrun-result">
            <p className="text-sm font-semibold text-sky-900 mb-2">Dry-run summary</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3 text-sm">
              <div className="flex justify-between"><span>Orphan leads</span><strong>{fmt(t.orphan_leads)}</strong></div>
              <div className="flex justify-between"><span>Chunks (500/op)</span><strong>{fmt(t.chunks)}</strong></div>
            </div>

            <p className="text-xs font-semibold uppercase tracking-wide text-sky-800 mt-2 mb-1">By stage</p>
            <div className="flex flex-wrap gap-2">
              {dryrun.breakdown_by_stage.map((b) => (
                <Badge key={b.stage} variant="outline" className="bg-white">
                  {STAGE_LABELS[b.stage]}: <strong className="ml-1">{fmt(b.count)}</strong>
                </Badge>
              ))}
            </div>

            <p className="text-xs font-semibold uppercase tracking-wide text-sky-800 mt-3 mb-1">By project</p>
            {dryrun.breakdown_by_project.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No orphan leads — all SV-stage leads already have a visit record.</p>
            ) : (
              <div className="space-y-1">
                {dryrun.breakdown_by_project.map((p) => (
                  <div key={p.project} className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">{p.project}</Badge>
                    <span>total <strong>{fmt(p.total)}</strong></span>
                    {SV_STAGE_KEYS.map(s => p.by_stage?.[s] ? (
                      <span key={s} className="text-slate-600">
                        {STAGE_LABELS[s]}: <strong>{fmt(p.by_stage[s])}</strong>
                      </span>
                    ) : null)}
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs font-semibold uppercase tracking-wide text-sky-800 mt-3 mb-1">Scheduled-date source distribution</p>
            <div className="flex flex-wrap gap-2 text-xs">
              {dryrun.breakdown_by_scheduled_date_source.map((b) => (
                <Badge key={b.source} variant="outline" className="bg-white font-mono">
                  {b.source}: <strong className="ml-1">{fmt(b.count)}</strong>
                </Badge>
              ))}
            </div>

            {dryrun.candidate_raw_visit_date_fields?.length > 0 && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2">
                <p className="text-xs font-semibold text-amber-800">Other date-like fields found on leads (not used in this run, FYI for future improvement):</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {dryrun.candidate_raw_visit_date_fields.map(f => (
                    <code key={f} className="text-xs bg-white px-1 rounded border">{f}</code>
                  ))}
                </div>
              </div>
            )}

            {dryrun.samples?.length > 0 && (
              <details className="mt-3" open>
                <summary className="text-xs font-semibold uppercase tracking-wide text-sky-800 cursor-pointer">
                  Sample (first {dryrun.samples.length}) — will create
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="text-left pr-2">Lead</th>
                        <th className="text-left pr-2">Project</th>
                        <th className="text-left pr-2">Stage</th>
                        <th className="text-left pr-2">New status</th>
                        <th className="text-left pr-2">Visit type</th>
                        <th className="text-left pr-2">Scheduled date (source)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dryrun.samples.map((s, i) => (
                        <tr key={i} className="border-t border-sky-100">
                          <td className="pr-2 py-1">
                            <p className="font-medium">{s.lead_name || "—"}</p>
                            <p className="text-[10px] text-slate-500">{s.lead_id}</p>
                          </td>
                          <td className="pr-2 py-1">{s.project}</td>
                          <td className="pr-2 py-1">{STAGE_LABELS[s.stage] || s.stage}</td>
                          <td className="pr-2 py-1">
                            <Badge className={STATUS_TONE[s.will_create.status] || ""}>
                              {s.will_create.status}
                            </Badge>
                          </td>
                          <td className="pr-2 py-1">{s.will_create.visit_type}</td>
                          <td className="pr-2 py-1">
                            {s.will_create.scheduled_date?.slice(0, 19).replace("T", " ")}
                            <span className="text-[10px] text-slate-500 ml-1">({s.will_create.scheduled_date_source})</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}

        {applyResult && (
          <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3" data-testid="vs-apply-result">
            <p className="text-sm font-semibold text-emerald-900 mb-2 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" /> Apply complete
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
              <div className="flex justify-between"><span>Matched</span><strong>{fmt(applyResult.totals.matched)}</strong></div>
              <div className="flex justify-between"><span>Created</span><strong className="text-emerald-700">{fmt(applyResult.totals.created)}</strong></div>
              <div className="flex justify-between"><span>Skipped (has visit)</span><strong>{fmt(applyResult.totals.skipped_already_has_visit)}</strong></div>
              <div className="flex justify-between"><span>Skipped (stage changed)</span><strong>{fmt(applyResult.totals.skipped_stage_changed)}</strong></div>
              <div className="flex justify-between"><span>Errors</span><strong className={applyResult.totals.errors > 0 ? "text-rose-700" : ""}>{fmt(applyResult.totals.errors)}</strong></div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              run_id <code>{applyResult.run_id}</code> · {applyResult.chunks_count} chunks · rollback removes only docs with this synthesis_run_id.
            </p>
          </div>
        )}

        {showRuns && (
          <div className="border border-slate-200 rounded-lg p-3" data-testid="vs-runs-history">
            <p className="text-sm font-semibold text-slate-800 mb-2">Run history (most recent first)</p>
            {runs.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No runs yet.</p>
            ) : (
              <div className="space-y-1">
                {runs.map((r) => {
                  const isApply = r.kind === "apply";
                  const totals = r.totals || {};
                  return (
                    <div key={r.run_id} className="border border-slate-100 rounded p-2 text-xs flex flex-wrap items-center gap-2">
                      <Badge className={isApply ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                        {r.kind}
                      </Badge>
                      <code className="text-slate-600">{r.run_id}</code>
                      <span className="text-slate-500">{r.started_at?.slice(0, 19).replace("T", " ")} UTC</span>
                      <span>by <strong>{r.performed_by_name || r.performed_by}</strong></span>
                      <span className="ml-auto text-slate-700">
                        {isApply
                          ? <>created <strong>{fmt(totals.created)}</strong> · skipped {fmt((totals.skipped_already_has_visit || 0) + (totals.skipped_stage_changed || 0))} · errors {fmt(totals.errors)}</>
                          : <>deleted <strong>{fmt(totals.deleted)}</strong> · errors {fmt(totals.errors)}</>}
                      </span>
                      {isApply && totals.created > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowRollbackConfirm(r)}
                          disabled={rollingBack === r.run_id}
                          data-testid={`vs-rollback-${r.run_id}`}
                          className="text-amber-700 border-amber-300 hover:bg-amber-50"
                        >
                          {rollingBack === r.run_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Undo2 className="w-3 h-3 mr-1" />}
                          Rollback
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={showApplyConfirm} onOpenChange={(o) => !o && setShowApplyConfirm(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Confirm Apply
            </DialogTitle>
            <DialogDescription>
              About to create <strong>{fmt(orphans)}</strong> synthesised <code>site_visits</code> records.
              Existing real visits are never overwritten. Each new record carries
              <code className="mx-1">is_synthesised: true</code> and the run_id so rollback removes only
              this batch. Type <code className="bg-slate-100 px-1">CONFIRM</code> below.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type CONFIRM"
            data-testid="vs-apply-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowApplyConfirm(false); setConfirmText(""); }}>Cancel</Button>
            <Button
              onClick={runApply}
              disabled={confirmText !== "CONFIRM"}
              data-testid="vs-apply-confirm-submit"
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
            >
              Apply now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showRollbackConfirm} onOpenChange={(o) => !o && setShowRollbackConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-amber-600" /> Confirm Rollback
            </DialogTitle>
            <DialogDescription>
              About to delete every synthesised visit from run
              <code className="mx-1">{showRollbackConfirm?.run_id}</code>
              (<strong>{fmt(showRollbackConfirm?.totals?.created)}</strong> records). Only documents with
              <code className="mx-1">is_synthesised: true</code> AND a matching synthesis_run_id are removed —
              real visits and feedback added by reps are never touched. Type
              <code className="bg-slate-100 px-1">ROLLBACK</code> below.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={rollbackText}
            onChange={(e) => setRollbackText(e.target.value)}
            placeholder="Type ROLLBACK"
            data-testid="vs-rollback-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRollbackConfirm(null); setRollbackText(""); }}>Cancel</Button>
            <Button
              onClick={() => runRollback(showRollbackConfirm)}
              disabled={rollbackText !== "ROLLBACK"}
              data-testid="vs-rollback-confirm-submit"
              className="bg-amber-600 hover:bg-amber-700 disabled:opacity-40"
            >
              Rollback now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const SV_STAGE_KEYS = ["site_visit_scheduled", "site_visit", "revisit"];
